import { useEffect, useRef } from "react";
import {
  restoredScrollOffset,
  savedPageDepth,
  shouldReplayPageDepth,
} from "./restoration";

/**
 * Put a keyset list back where the reader left it: same page depth, same
 * scroll offset, same focused card.
 *
 * The failure this removes is specific to keyset pagination. Opening one item
 * and coming back re-runs the query from the first cursor, so a reviewer four
 * pages into a queue lands at the top of page one — every single time. That is
 * not a polish problem; it is what makes a long queue unworkable.
 *
 * `key` MUST name the organization and the filter set together. Two filter
 * sets are two different lists, and restoring one into the other drops the
 * reader at an offset they have never visited.
 */
export function useListRestoration({
  key,
  cardIDPrefix,
  pageCount,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  key: string;
  /** Prefix of the `id` each card renders, so the right one can be refocused. */
  cardIDPrefix: string;
  /** Zero until the first page resolves. */
  pageCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}): void {
  const restored = useRef(false);
  // The departure handler must read the depth through a ref: an effect with a
  // `[key]` dependency closes over the FIRST render's value, which is page one
  // no matter how far the reader actually paged.
  const pageCountRef = useRef(pageCount);
  pageCountRef.current = pageCount;

  useEffect(
    () => () => {
      sessionStorage.setItem(key, String(window.scrollY));
      sessionStorage.setItem(`${key}:depth`, String(pageCountRef.current));
    },
    [key],
  );

  // A new key is a different list, so the position already restored for the
  // previous one must not suppress this one's.
  useEffect(() => {
    restored.current = false;
  }, [key]);

  useEffect(() => {
    if (pageCount === 0 || restored.current || isFetchingNextPage) return;
    const savedDepth = savedPageDepth(sessionStorage.getItem(`${key}:depth`));
    if (shouldReplayPageDepth(pageCount, savedDepth, hasNextPage)) {
      // One page per pass. Each fetch re-runs this effect, so the scroll
      // offset below is only ever applied to a list long enough to hold it —
      // scrolling to 4000px on a 900px list silently lands at the bottom.
      void fetchNextPage();
      return;
    }
    restored.current = true;
    const priorOffset = sessionStorage.getItem(key);
    const selectedID = sessionStorage.getItem(`${key}:selected`);
    // After paint, or the browser's own scroll restoration runs afterwards and
    // wins.
    requestAnimationFrame(() => {
      const prior = restoredScrollOffset(priorOffset);
      if (prior !== null) window.scrollTo(0, prior);
      if (selectedID === null) return;
      document
        .getElementById(`${cardIDPrefix}${selectedID}`)
        ?.focus({ preventScroll: true });
    });
  }, [
    cardIDPrefix,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    key,
    pageCount,
  ]);
}
